import React, { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { Timestamp } from "firebase/firestore";

import { PersonModelInterface, Payment } from "@/models/personModel";
import { PaymentData } from "../../Payment/PaymentsPopup";
import { usePeople } from "@/context/PeopleContext";
import { calculateLoanDetails } from "@/utils/loanCalculation";

import FormFields from "../LoanCalculation/LoanCalculationFields";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
    CreditCard,
    Wallet,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";

import Summary from "./Summary";
import Schedule from "./Schedule";
import PaymentsSection from "./PaymanentSection";
import EarlyClosePopup from "../../EarlyPopups/EarlyClosePopup";

interface LoanCalculationFormsProps {
    showSchedule?: boolean;
    isDetailView?: boolean;
}

const statusConfig: Record<
    string,
    { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
    new: { label: "Новый", variant: "outline" },
    pending: { label: "В процессе", variant: "default" },
    overdue: { label: "Просрочено", variant: "destructive" },
    closed: { label: "Закрыт", variant: "secondary" },
    closedEarly: { label: "Закрыт досрочно", variant: "secondary" },
    returned: { label: "Возврат", variant: "secondary" },
};

function StatusBadge({ status }: { status?: string }) {
    const cfg = status ? statusConfig[status] : undefined;
    const label = cfg?.label ?? (status || "—");
    const variant = cfg?.variant ?? "outline";

    const colorClass =
        status === "new"
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : status === "pending"
                ? "border-amber-200 bg-amber-50 text-amber-700"
                : ["overdue", "closed", "closedEarly"].includes(String(status))
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "";

    return (
        <Badge variant={variant} className={colorClass}>
            {label}
        </Badge>
    );
}

const formatSom = (v: number) =>
    new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "KGS",
        minimumFractionDigits: 0,
    }).format(v);

const LoanCalculationForms: React.FC<LoanCalculationFormsProps> = ({
                                                                       showSchedule = true,
                                                                       isDetailView = false,
                                                                   }) => {
    const { setValue, getValues } = useFormContext<PersonModelInterface>();
    const navigate = useNavigate();
    const { updatePerson } = usePeople();

    const phonePrice = Number(useWatch({ name: "creditDetail.phoneAmount" }) || 0);
    const initialPayment = Number(useWatch({ name: "creditDetail.downPayment" }) || 0);
    const term = Number(useWatch({ name: "creditDetail.term" }) || 0);
    const interest = Number(useWatch({ name: "creditDetail.percent" }) || 0);
    const creditDate = String(useWatch({ name: "creditDetail.releaseDay" }) || "");
    const productionCost = Number(useWatch({ name: "phone.costPrice" }) || 0);
    const status = String(useWatch({ name: "status" }) || "new");

    const rawPayments = useWatch({ name: "payments" }) as Payment[] | undefined;
    const payments = useMemo(() => rawPayments || [], [rawPayments]);

    const [openPaymentPopup, setOpenPaymentPopup] = useState(false);
    const [openEarlyClosePopup, setOpenEarlyClosePopup] = useState(false);
    const [earlyCloseDate, setEarlyCloseDate] = useState<string | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);

    const earlyClosed = useMemo(
        () => ["closedEarly", "returned", "closed"].includes(status),
        [status]
    );

    useEffect(() => {
        if (earlyClosed && !earlyCloseDate) {
            const closing = getValues("creditDetail.closingDay") as unknown as Timestamp | null;
            if (closing?.toDate) {
                setEarlyCloseDate(dayjs(closing.toDate()).format("YYYY-MM-DD"));
            }
        }
    }, [earlyClosed, earlyCloseDate, getValues]);

    const calc = useMemo(() => {
        return calculateLoanDetails(
            phonePrice,
            initialPayment,
            term,
            interest,
            creditDate,
            productionCost,
            payments,
            earlyClosed,
            earlyCloseDate,
            status,
            selectedOption
        );
    }, [
        phonePrice,
        initialPayment,
        term,
        interest,
        creditDate,
        productionCost,
        payments,
        earlyClosed,
        earlyCloseDate,
        status,
        selectedOption,
    ]);

    const {
        total,
        monthlyPayment,
        closingDate,
        schedule,
        netProfit,
        remainingDebt,
        paidByClient,
        discountApplied,
    } = calc;

    useEffect(() => {
        if (!(phonePrice > 0 && initialPayment >= 0 && term && interest && creditDate)) return;

        const currentValues = getValues();

        if (currentValues.creditDetail?.total !== total) {
            setValue("creditDetail.total", Number(total.toFixed(2)));
        }

        if (currentValues.creditDetail?.monthlyPayment !== (earlyClosed ? 0 : monthlyPayment)) {
            setValue("creditDetail.monthlyPayment", Number((earlyClosed ? 0 : monthlyPayment).toFixed(2)));
        }

        if (currentValues.creditDetail?.netProfit !== netProfit) {
            setValue("creditDetail.netProfit", Number(netProfit.toFixed(2)));
        }

        if (currentValues.creditDetail?.remainingDebt !== remainingDebt) {
            setValue("creditDetail.remainingDebt", Number(remainingDebt.toFixed(2)));
        }

        if (closingDate) {
            const closingTimestamp = Timestamp.fromDate(new Date(closingDate));
            setValue("creditDetail.closingDay", closingTimestamp);
        }

        if (
            !earlyClosed &&
            remainingDebt <= 0.01 &&
            !["closedEarly", "returned"].includes(status)
        ) {
            const todayISO = dayjs().locale("ru").format("YYYY-MM-DD");
            const todayTimestamp = Timestamp.fromDate(new Date(todayISO));

            setValue("status", "closed");
            setValue("creditDetail.remainingDebt", 0);
            setValue("creditDetail.monthlyPayment", 0);
            setValue("creditDetail.closingDay", todayTimestamp);
        }
    }, [
        phonePrice,
        initialPayment,
        term,
        interest,
        creditDate,
        total,
        monthlyPayment,
        netProfit,
        remainingDebt,
        closingDate,
        earlyClosed,
        status,
        setValue,
        getValues,
    ]);

    const canEarlyClose = useMemo(() => {
        return isDetailView && Boolean(creditDate) && !earlyClosed;
    }, [isDetailView, creditDate, earlyClosed]);

    const paymentCalculations = useMemo(() => {
        const earlyCloseRemaining = Math.max(
            0,
            phonePrice - (initialPayment + paidByClient)
        );

        return {
            totalPaid: paidByClient,
            earlyCloseRemaining,
        };
    }, [phonePrice, initialPayment, paidByClient]);

    const handlePaymentSave = async (payment: PaymentData) => {
        if (payment.type === "discount") {
            const safeDiscount = Math.max(0, Number(payment.amount || 0));
            const currentRemainingDebt = Number(getValues("creditDetail.remainingDebt") || 0);
            const appliedDiscount = Math.min(safeDiscount, currentRemainingDebt);

            if (!appliedDiscount) {
                toast.error("Скидка не может быть больше остатка долга");
                return;
            }

            const todayISO = payment.date || dayjs().format("YYYY-MM-DD");

            const discountPayment: Payment = {
                id: payment.id || uuidv4(),
                date: todayISO,
                amount: appliedDiscount,
                type: "discount",
            };

            const currentData = getValues();
            const updatedPayments = [...(currentData.payments || []), discountPayment];

            setValue("payments", updatedPayments);
            setOpenPaymentPopup(false);

            try {
                await updatePerson({
                    ...currentData,
                    payments: updatedPayments,
                });

                toast.success(`Скидка ${appliedDiscount.toFixed(2)} сом применена`);
            } catch (e) {
                console.error("Ошибка применения скидки", e);
                toast.error("Ошибка применения скидки");
            }

            return;
        }

        const newPayment: Payment = {
            ...payment,
            amount: Math.max(0, Number(payment.amount || 0)),
            type: "payment",
        };

        const updatedPayments = [...payments, newPayment];

        setValue("hasPayment", true);
        setValue("payments", updatedPayments);
        setOpenPaymentPopup(false);

        const futureCalc = calculateLoanDetails(
            phonePrice,
            initialPayment,
            term,
            interest,
            creditDate,
            productionCost,
            updatedPayments,
            false,
            null,
            status,
            selectedOption
        );

        let newStatus = status;

        const realPaymentsBefore = payments.filter((p) => p.type !== "discount");

        if (realPaymentsBefore.length === 0 && !earlyClosed) {
            newStatus = "pending";
            setValue("status", "pending");
        }

        if (futureCalc.remainingDebt <= 0.01 && !["closedEarly", "returned"].includes(status)) {
            const todayISO = dayjs().locale("ru").format("YYYY-MM-DD");
            const todayTimestamp = Timestamp.fromDate(new Date(todayISO));

            newStatus = "closed";
            setValue("status", "closed");
            setValue("creditDetail.closingDay", todayTimestamp);
            setValue("creditDetail.remainingDebt", 0);
            setValue("creditDetail.monthlyPayment", 0);
        }

        try {
            const currentData = getValues();
            const updatedData: PersonModelInterface = {
                ...currentData,
                status: newStatus,
                creditDetail: {
                    ...currentData.creditDetail,
                    remainingDebt: Number(futureCalc.remainingDebt.toFixed(2)),
                },
                payments: updatedPayments,
                hasPayment: true,
            };

            await updatePerson(updatedData);

            if (newStatus === "closed") {
                toast.success("Кредит полностью оплачен и закрыт");
                navigate("/people");
            } else if (realPaymentsBefore.length === 0 && newStatus === "pending") {
                toast.success("Первый платеж добавлен, статус изменен");
            }
        } catch (e) {
            console.error("Ошибка при сохранении платежа", e);
            toast.error("Ошибка при сохранении платежа");
        }
    };

    const handleEarlyCloseConfirm = async (option: string) => {
        const todayISO = dayjs().locale("ru").format("YYYY-MM-DD");
        const todayTimestamp = Timestamp.fromDate(new Date(todayISO));

        setEarlyCloseDate(todayISO);
        setSelectedOption(option);

        let totalLocal: number;
        let netProfitLocal: number;
        let remainingToPay: number;
        let newStatus: string;

        if (option === "return") {
            totalLocal = initialPayment;
            netProfitLocal = Math.max(0, initialPayment - discountApplied);
            remainingToPay = Math.max(0, totalLocal - initialPayment - paidByClient);
            newStatus = "returned";
        } else {
            totalLocal = phonePrice;
            netProfitLocal = Math.max(0, totalLocal - productionCost - discountApplied);
            remainingToPay = Math.max(0, totalLocal - initialPayment - paidByClient);
            newStatus = "closedEarly";
        }

        setValue("creditDetail.total", Number(totalLocal.toFixed(2)));
        setValue("creditDetail.monthlyPayment", 0);
        setValue("creditDetail.closingDay", todayTimestamp);
        setValue("creditDetail.netProfit", Number(netProfitLocal.toFixed(2)));
        setValue("creditDetail.remainingDebt", 0);
        setValue("status", newStatus);

        const needAutoPayment = remainingToPay > 0;
        const autoPayment: Payment = {
            id: uuidv4(),
            date: todayISO,
            amount: remainingToPay,
            type: "payment",
        };

        try {
            const currentData = getValues();
            const updatedData: PersonModelInterface = {
                ...currentData,
                hasPayment: needAutoPayment ? true : currentData.hasPayment,
                creditDetail: {
                    ...currentData.creditDetail,
                    total: Number(totalLocal.toFixed(2)),
                    monthlyPayment: 0,
                    closingDay: todayTimestamp,
                    netProfit: Number(netProfitLocal.toFixed(2)),
                    remainingDebt: 0,
                },
                status: newStatus,
                payments: needAutoPayment ? [...payments, autoPayment] : payments,
            };

            await updatePerson(updatedData);

            toast.success(
                `Кредит успешно ${
                    newStatus === "returned" ? "возвращен" : "закрыт досрочно"
                }`
            );
            navigate("/people");
        } catch (e) {
            console.error("Ошибка при обработке операции", e);
            toast.error("Ошибка при обработке операции");
        } finally {
            setOpenEarlyClosePopup(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-base sm:text-lg font-semibold">Кредит</h2>
                    <StatusBadge status={status} />
                </div>
            </div>

            <Card className="rounded-2xl">
                <CardContent className="pt-6 space-y-6">
                    <FormFields />

                    <Separator />

                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Wallet className="h-4 w-4 text-muted-foreground" />
                            <p className="text-sm font-medium">Итоги</p>
                        </div>
                        <Summary
                            total={total}
                            monthlyPayment={monthlyPayment}
                            netProfit={netProfit}
                            closingDate={closingDate}
                            creditDate={creditDate}
                            earlyClosed={earlyClosed}
                            status={status}
                            earlyCloseDate={earlyCloseDate}
                            creditDetailClosingDay={
                                getValues("creditDetail.closingDay") as unknown as Timestamp
                            }
                        />
                    </div>

                    {phonePrice <= 0 || !term || !interest || !creditDate ? (
                        <div className="flex items-start gap-2 rounded-xl border p-3 text-sm text-muted-foreground">
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                            Заполни сумму телефона, взнос, срок, процент и дату, чтобы увидеть график и расчёты.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="flex items-start gap-2 rounded-xl border p-3 text-sm">
                                <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600" />
                                <div className="text-muted-foreground">
                                    Остаток долга:{" "}
                                    <span className="font-medium text-foreground">
                    {formatSom(Number(remainingDebt ?? 0))}
                  </span>
                                </div>
                            </div>

                            {discountApplied > 0 ? (
                                <div className="text-xs text-muted-foreground">
                                    Списано скидками: {formatSom(discountApplied)}
                                </div>
                            ) : null}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Schedule
                showSchedule={showSchedule}
                schedule={schedule}
                earlyClosed={earlyClosed}
                status={status}
                canEarlyClose={canEarlyClose}
                onEarlyClose={() => setOpenEarlyClosePopup(true)}
            />

            <PaymentsSection
                isDetailView={isDetailView}
                payments={payments}
                remainingDebt={remainingDebt ?? 0}
                earlyClosed={earlyClosed}
                status={status}
                openPaymentPopup={openPaymentPopup}
                onAddPayment={() => setOpenPaymentPopup(true)}
                onClosePaymentPopup={() => setOpenPaymentPopup(false)}
                onSavePayment={handlePaymentSave}
            />

            <EarlyClosePopup
                open={openEarlyClosePopup}
                remaining={paymentCalculations.earlyCloseRemaining}
                initialAmount={initialPayment}
                onClose={() => setOpenEarlyClosePopup(false)}
                onConfirm={handleEarlyCloseConfirm}
            />
        </div>
    );
};

export default LoanCalculationForms;